import { ApiRequest } from 'utils/ApiUtils';
const { buildYup } = require("json-schema-to-yup");

/**
 * format overrides between jsonSchema and yupSchema
 * @param {object} jsonSchema 
 */
function formatErrorFixes(jsonSchema) {
  const unModifiedSchema = deepCopy(jsonSchema);
  const modifiedSchema = deepCopy(unModifiedSchema);
  const iter = makePropsIterator(modifiedSchema);
  let errMessages = {};

  for (const { key, props } of iter) {
    if (props.format === "email") {
      modifiedSchema.properties[key].pattern = "^\\w+([\\.-]?\\w+)*@\\w+([\\.-]?\\w+)*(\\.\\w{2,3})+$";
      delete modifiedSchema.properties[key].format;
      errMessages[key] = { pattern: `${key} must be a valid email address` };
    };
    if (key.toLowerCase().includes("phone")) {
      errMessages[key] = { pattern: `${key} must match the following format XXX-XXX-XXXX` };
    };
    if (props.oneOf) {
      // need to convert to list of strings for yup validation transformation.
      if (typeof (props.oneOf[0]) == "object") {
        modifiedSchema.properties[key].oneOf = props.oneOf.map(r => r.const);
        errMessages[key] = { oneOf: `${key} must contain a valid value` };
      }
    };
    if (props.format === "date") {
      errMessages[key] = { nullable: `${key} is a required field` };
    };
  };
  return [modifiedSchema, errMessages];
}

/**
 * function to create a javascript iterator for props in a json schema.
 * nicer than looping through Object.key() every time.
 * @param {object} jsonSchema 
 */
function* makePropsIterator(jsonSchema) {
  const keys = Object.keys(jsonSchema.properties);
  const end = keys.length;
  const start = 0;
  const step = 1;
  let iterationCount = 0;
  for (let i = start; i < end; i += step) {
    iterationCount++;
    const key = keys[i];
    yield { key: key, props: jsonSchema.properties[key] }
  };
  return iterationCount;
};

function buildYupSchema(jsonSchema) {
  let [_jsonSchema, errMessages] = formatErrorFixes(jsonSchema);

  const config = {
    errMessages
  };

  const yupSchema = buildYup(_jsonSchema, config);
  return yupSchema;
};


function buildReference($ref, domainResponse, constant, title) {
  domainResponse.sort((a, b) => { if (a[title] < b[title]) return -1; if (a[title] > b[title]) return 1; return 0 });
  const domains = domainResponse.map(r => ({ "const": r[constant], "title": r[title] }));
  const reference = {
    [$ref] : { oneOf: domains }
  }
  return reference;
}


async function buildReferences(jsonSchema) {
  const iter = makePropsIterator(jsonSchema);
  const promises = [];
  for (const { props } of iter) {
    if (props.$ref) {
      promises.push(resolveDomainRef(props.$ref));
    };
  };
  const ready = await Promise.all(promises);
  const references = ready.reduce((accumulator, response) => {
    let reference = {};
    if (response.data !== undefined) {
      const { ref, data } = response;
      if(data.length > 0) {
        if(Object.keys(data[0]).includes('domainId')) {
          reference = buildReference(ref, data, 'domainId', 'description');
        } else if(Object.keys(data[0]).includes('userGroupId')) {
          reference = buildReference(ref, data, 'userGroupId', 'description');
        }
      }
    } else {
      const { ref, value } = response;
      value.sort((a, b) => { if (a.Name < b.Name) return -1; if (a.Name > b.Name) return 1; return 0 });
      reference = buildReference(ref, value, 'OrganizationId', 'Name')   
    }
    return {...accumulator, ...reference}
  }, {});
  return references;
}

function unpackReferences(jsonSchema, references) {
  const iter = makePropsIterator(jsonSchema);
  for (const { key, props } of iter) {
    if (props.$ref) {
      const reference = references[props.$ref];
      jsonSchema.properties[key] = {
        ...jsonSchema.properties[key],
        ...reference
      };
    }
  };
  return jsonSchema;
}

const deepCopy = (inObject) => {
  let outObject, value, key

  if (typeof inObject !== "object" || inObject === null) {
    return inObject // Return the value if inObject is not an object
  }

  // Create an array or object to hold the values
  outObject = Array.isArray(inObject) ? [] : {}

  for (key in inObject) {
    value = inObject[key]

    // Recursively (deep) copy for nested objects, including arrays
    outObject[key] = deepCopy(value)
  }

  return outObject
}

async function resolveDomainRef(refUrl) {
  const url = refUrl.replace("/api", "");
  const resp =  await ApiRequest(url, '');
  return {
    ...resp,
    ref: refUrl
  }
}

class Schema {
  constructor(title) {
    this['$id'] = `https://mfs-portal.azurewebsite.net/${title}.schema.json`;
    this['$schema'] = "http://json-schema.org/draft-07/schema#";
    this.title = title;
    this.type = 'object';
    this.properties = {};
    this.required = [];
  }

  addField(field) {
    const fieldObj = {};
    fieldObj[field.fieldName] = field.asObject();
    this.properties = {
      ...this.properties,
      ...field.asObject()
    };
    if (field.required) {
      this.required.push(field.fieldName);
    }
  }

  getDisabled() {
    const disabled = [];
    const iter = makePropsIterator(this);
    for (const { key, props } of iter) {
      props.disabled && disabled.push(key);
    };
    return disabled;
  }
}

class Field {
  static fieldError = (name) => new Error(`parameter '${name}' is required to create a JSON schema field Class`);
  constructor({ fieldName, type, label, format = null, nullable = null, disabled= null, required = null, defaultValue = null, max = null, min = null, enumValues = null, oneOf = null, $ref=null }) {
    if (!fieldName) throw Field.fieldError('fieldName');
    if (!type) throw Field.fieldError('type');
    if (!label) throw Field.fieldError('label');
    this.fieldName = fieldName;
    this.type = type;
    this.label = label;
    if (defaultValue) this.default = defaultValue;
    if (disabled) this.disabled = disabled;
    if (format) this.format = format;
    if (nullable) this.nullable = nullable;
    if (required) this.required = required;
    if (max) this.max = max;
    if (min) this.min = min;
    if (enumValues) this.enum = enumValues;
    if (oneOf) this.oneOf = oneOf;
    if($ref) this.$ref = $ref;
  };

  asObject() {
    const fieldObj = { [this.fieldName]: Object.keys(this).map(r => ({ [r]: this[r] })).reduce((a, b) => ({ ...a, ...b })) };
    delete fieldObj[this.fieldName].fieldName;
    delete fieldObj[this.fieldName].required;
    return fieldObj;
  }
}

const getHiddenProperties = (schema) => {
  const iter = makePropsIterator(schema);
  const hiddenProps = [];
  for (const { key, props } of iter) {
    if(props.options && props.options.hidden) {
      hiddenProps.push(key);
    }
  };
  return hiddenProps;
}

const patchDefaultValues = (defaultValues, formValues) => {
  const payload = defaultValues;
  Object.entries(payload).forEach(([key, value]) => {
      if(key in formValues) {
          payload[key] = formValues[key]
      };
  });

  return payload;
}


export { makePropsIterator, buildYupSchema, deepCopy, buildReference, buildReferences, unpackReferences, Schema, Field, resolveDomainRef, getHiddenProperties, patchDefaultValues };